/**
 * Régua canônica de classificação de sensibilidade — 4 níveis (1-4).
 *
 * Fonte da verdade triangulada (R15):
 * - **Escala numérica + evento de escalação** entre níveis: ADR-024
 *   (`classification.escalated { from: nivel, to: nivel }`); `classificationLevel`
 *   Int 1-4 em `Conversation`/`QualitySignals` no schema Prisma.
 * - **Rótulos + critérios de cada nível**: prompt ATIVO do `classification-agent`
 *   (`apps/engine/src/modules/ai-orchestration/prompts/classification-agent/v2.json`
 *   — o `v1.json` é retido como versão histórica para reprodutibilidade forense de
 *   classificações feitas sob `agentVersion 1.0.0`), que é quem efetivamente produz
 *   o `sensitivityLevel` em runtime (LLM semântico, ADR-036 §4). A classificação
 *   NÃO usa lista de palavras-chave: é semântica. Um teste-guard no Engine
 *   (`classification-rubric-consistency.spec.ts`) trava a divergência entre esta
 *   régua e o prompt ativo (o ponto que driftou na v3.240).
 * - **Nível 4 = escalação dura não-removível**: ADR-030 Eixo I ("Nível 4 sempre
 *   escala" — proteção não-removível que preserva a essência ética do P5).
 *
 * Esta constante canoniza, num único lugar consumível por Engine + frontends, o
 * rubric que hoje vive embutido no prompt do agente. A fiação do contexto de nicho
 * ao classificador (achado "(b)") foi MERGEADA na v3.240 (o prompt v2.json calibra
 * a sensibilidade pelo setor do tenant). Resta DIFERIDO o refactor de fazer o
 * prompt CONSUMIR esta constante (eliminando a duplicação do rubric inline); o
 * teste-guard citado acima protege contra drift enquanto a duplicação existir.
 *
 * Nota de honestidade: a tela "Configuração de Classificação" é, por ora,
 * read-only (entender a régua + a garantia do Nível 4). Não há gatilho
 * keyword→nível configurável pelo tenant porque o runtime classifica
 * semanticamente; cadastrar termos não teria consumidor (honest-defer).
 */
export const CLASSIFICATION_LEVELS = [1, 2, 3, 4];
/** Slug estável por nível — para i18n/estilo no frontend sem acoplar ao número. */
export const CLASSIFICATION_LEVEL_KEYS = ['publica', 'restrita', 'sensivel', 'critica'];
export const CLASSIFICATION_LEVELS_CATALOG = [
    {
        level: 1,
        key: 'publica',
        label: 'Pública',
        description: 'Saudações e perguntas genéricas, sem informação sensível.',
        tone: 'success',
        hardEscalation: false,
    },
    {
        level: 2,
        key: 'restrita',
        label: 'Restrita',
        description: 'Identificação básica e informações de serviço.',
        tone: 'neutral',
        hardEscalation: false,
    },
    {
        level: 3,
        key: 'sensivel',
        label: 'Sensível',
        description: 'Financeiro, contratual, conflito, ameaça de cancelamento ou dados pessoais — inclui dados pessoais sensíveis (saúde, biometria, origem racial/étnica, religião, opinião política, vida sexual — LGPD Art. 11).',
        tone: 'warning',
        hardEscalation: false,
    },
    {
        level: 4,
        key: 'critica',
        label: 'Crítica',
        description: 'Ameaça jurídica/regulatória (processo, Anatel, ANPD), risco de vida, ideação suicida, ato ilegal grave ou ameaça direta a terceiros. P1 — escala imediata para humano.',
        tone: 'danger',
        hardEscalation: true,
    },
];
/**
 * Nível que dispara escalação dura não-removível (ADR-030). Exposto como
 * constante para que enforcement não dependa de varrer o catálogo.
 */
export const HARD_ESCALATION_LEVEL = 4;
const CATALOG_BY_LEVEL = CLASSIFICATION_LEVELS_CATALOG.reduce((acc, def) => {
    acc[def.level] = def;
    return acc;
}, {});
export function getClassificationLevelDefinition(level) {
    return CATALOG_BY_LEVEL[level];
}
export function isHardEscalationLevel(level) {
    return level >= HARD_ESCALATION_LEVEL;
}
const _assertFourLevels = true;
void _assertFourLevels;
/** O Nível 4 (e só ele) é hardEscalation. */
const _assertHardLevel = true;
void _assertHardLevel;
const _assertSoftLevel = false;
void _assertSoftLevel;
//# sourceMappingURL=catalog.js.map